import React, { useState, useEffect } from 'react';
import { firestore } from '../firebaseConfig';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import './UploadLogs.css';

const UploadLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchAllConsentLogs();
  }, []);

  const fetchAllConsentLogs = async () => {
    setLoading(true);
    try {
      // Zuerst alle User aus der World Collection holen
      const worldSnapshot = await getDocs(collection(firestore, 'World'));
      const allLogs = [];

      // Für jeden User die consent_logs abrufen
      for (const userDoc of worldSnapshot.docs) {
        const userId = userDoc.id;
        const consentLogsRef = collection(firestore, `World/${userId}/consent_logs`);
        
        try {
          const logsSnapshot = await getDocs(consentLogsRef);
          
          logsSnapshot.docs.forEach(logDoc => {
            const logData = logDoc.data();
            allLogs.push({
              id: logDoc.id,
              ...logData,
              // Formatiertes Datum für die Anzeige
              formatted_date: logData.timestamp ? formatDate(logData.timestamp) : 'N/A'
            });
          });
        } catch (err) {
          // Keine consent_logs für diesen User - überspringen
          console.log(`No consent logs for user ${userId}`);
        }
      }

      // Nach Zeitstempel sortieren (neueste zuerst)
      allLogs.sort((a, b) => {
        const dateA = new Date(a.timestamp || 0);
        const dateB = new Date(b.timestamp || 0);
        return dateB - dateA;
      });

      setLogs(allLogs);
    } catch (error) {
      console.error('Error fetching consent logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const filteredAndSortedLogs = logs
    .filter(log => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        (log.user_email && log.user_email.toLowerCase().includes(search)) ||
        (log.file_name && log.file_name.toLowerCase().includes(search)) ||
        (log.ip_address && log.ip_address.includes(search))
      );
    })
    .sort((a, b) => {
      let valueA = a[sortField];
      let valueB = b[sortField];
      
      if (sortField === 'timestamp') {
        valueA = new Date(valueA || 0);
        valueB = new Date(valueB || 0);
      } else if (sortField === 'entries_imported') {
        valueA = valueA || 0;
        valueB = valueB || 0;
      } else {
        valueA = (valueA || '').toString().toLowerCase();
        valueB = (valueB || '').toString().toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return valueA > valueB ? 1 : -1;
      }
      return valueA < valueB ? 1 : -1;
    });

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="upload-logs-container">
      <div className="upload-logs-header">
        <h2>📋 Upload Logs</h2>
        <p className="upload-logs-subtitle">GDPR-compliant overview of all contact list uploads</p>
      </div>

      <div className="upload-logs-controls">
        <div className="upload-logs-search">
          <input
            type="text"
            placeholder="Search by email, filename or IP..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="upload-logs-search-input"
          />
        </div>
        <button 
          className="upload-logs-refresh-btn"
          onClick={fetchAllConsentLogs}
          disabled={loading}
        >
          {loading ? '⏳ Loading...' : '🔄 Refresh'}
        </button>
      </div>

      <div className="upload-logs-stats">
        <div className="upload-logs-stat-card">
          <span className="stat-number">{logs.length}</span>
          <span className="stat-label">Total Uploads</span>
        </div>
        <div className="upload-logs-stat-card">
          <span className="stat-number">{new Set(logs.map(l => l.user_email)).size}</span>
          <span className="stat-label">Unique Users</span>
        </div>
        <div className="upload-logs-stat-card">
          <span className="stat-number">{logs.reduce((sum, l) => sum + (l.entries_imported || 0), 0).toLocaleString()}</span>
          <span className="stat-label">Total Entries Imported</span>
        </div>
      </div>

      {loading ? (
        <div className="upload-logs-loading">
          <div className="upload-logs-spinner"></div>
          <p>Loading upload logs...</p>
        </div>
      ) : filteredAndSortedLogs.length === 0 ? (
        <div className="upload-logs-empty">
          <p>📭 No upload logs found</p>
          {searchTerm && <p className="upload-logs-empty-hint">Try adjusting your search</p>}
        </div>
      ) : (
        <div className="upload-logs-table-wrapper">
          <table className="upload-logs-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('timestamp')} className="sortable">
                  Date {getSortIcon('timestamp')}
                </th>
                <th onClick={() => handleSort('user_email')} className="sortable">
                  User Email {getSortIcon('user_email')}
                </th>
                <th onClick={() => handleSort('file_name')} className="sortable">
                  File Name {getSortIcon('file_name')}
                </th>
                <th onClick={() => handleSort('entries_imported')} className="sortable">
                  Entries {getSortIcon('entries_imported')}
                </th>
                <th onClick={() => handleSort('ip_address')} className="sortable">
                  IP Address {getSortIcon('ip_address')}
                </th>
                <th>Consent</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedLogs.map((log, index) => (
                <tr key={log.id || index}>
                  <td className="upload-logs-date">{log.formatted_date}</td>
                  <td className="upload-logs-email">{log.user_email || 'N/A'}</td>
                  <td className="upload-logs-filename" title={log.file_name}>
                    {log.file_name ? (log.file_name.length > 30 ? log.file_name.substring(0, 30) + '...' : log.file_name) : 'N/A'}
                  </td>
                  <td className="upload-logs-entries">
                    <span className="entries-badge">{(log.entries_imported || 0).toLocaleString()}</span>
                  </td>
                  <td className="upload-logs-ip">{log.ip_address || 'N/A'}</td>
                  <td className="upload-logs-consent">
                    <span className="consent-badge" title={log.consent_text}>✓ Accepted</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="upload-logs-footer">
        <p>Showing {filteredAndSortedLogs.length} of {logs.length} logs</p>
      </div>
    </div>
  );
};

export default UploadLogs;

